import { useState } from 'react';
import { useFinancieelStore } from '../../stores';
import type { OmzetRegel, GrootboekMapping } from '../../stores/financieel-store';

function formatAmount(amount: number): string {
  return new Intl.NumberFormat('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
}

function formatMonthLabel(month: string): string {
  const parts = month.split('-').map(Number);
  const d = new Date(parts[0]!, parts[1]! - 1, 1);
  const label = d.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

interface GroupedRow {
  key: string;               // unique key for React (e.g. "8000-B2C")
  grootboek_nummer: string;
  grootboek_naam: string;
  segment_type: 'B2C' | 'B2B' | null;  // null = no split needed
  total_excl: number;
  total_incl: number;
  invoice_count: number;
  subRows: OmzetRegel[];
}

function isB2B(row: OmzetRegel): boolean {
  const name = row.product_name?.toLowerCase() || '';
  return name.includes('ak -') || name.includes('wederverkoper');
}

function groupByGrootboek(omzetData: OmzetRegel[], mapping: GrootboekMapping[]): GroupedRow[] {
  // First pass: group by grootboek + B2B/B2C
  const groups = new Map<string, GroupedRow>();

  for (const row of omzetData) {
    const match = mapping.find(m =>
      m.segment === row.segment &&
      (m.category === null || m.category === row.category) &&
      (m.product_name === null || m.product_name === row.product_name)
    ) || mapping.find(m =>
      m.segment === row.segment && m.category === null && m.product_name === null
    );

    const nummer = match?.grootboek_nummer || '9999';
    const naam = match?.grootboek_naam || 'Overig';
    const b2b = isB2B(row);
    const key = `${nummer}-${b2b ? 'B2B' : 'B2C'}`;

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        grootboek_nummer: nummer,
        grootboek_naam: naam,
        segment_type: b2b ? 'B2B' : 'B2C',
        total_excl: 0,
        total_incl: 0,
        invoice_count: 0,
        subRows: [],
      });
    }

    const group = groups.get(key)!;
    group.total_excl += row.total_excl;
    group.total_incl += row.total_incl;
    group.invoice_count += row.invoice_count;
    group.subRows.push(row);
  }

  // Second pass: if a grootboek only has B2C, remove the label
  const nummerCounts = new Map<string, number>();
  for (const g of groups.values()) {
    nummerCounts.set(g.grootboek_nummer, (nummerCounts.get(g.grootboek_nummer) || 0) + 1);
  }
  for (const g of groups.values()) {
    if (nummerCounts.get(g.grootboek_nummer) === 1) {
      g.segment_type = null; // no split needed, don't show label
    }
  }

  return Array.from(groups.values())
    .sort((a, b) => a.key.localeCompare(b.key));
}

function exportCSV(grouped: GroupedRow[], month: string) {
  const BOM = '\uFEFF';
  const header = 'Grootboek nummer;Grootboek naam;Excl. BTW;Incl. BTW;Aantal';
  const rows = grouped.map(g => {
    const formatNL = (n: number) => n.toFixed(2).replace('.', ',');
    const naam = g.segment_type ? `${g.grootboek_naam} (${g.segment_type})` : g.grootboek_naam;
    return `${g.grootboek_nummer};${naam};${formatNL(g.total_excl)};${formatNL(g.total_incl)};${g.invoice_count}`;
  });

  const totalExcl = grouped.reduce((s, g) => s + g.total_excl, 0);
  const totalIncl = grouped.reduce((s, g) => s + g.total_incl, 0);
  const totalCount = grouped.reduce((s, g) => s + g.invoice_count, 0);
  rows.push(`;;${totalExcl.toFixed(2).replace('.', ',')};${totalIncl.toFixed(2).replace('.', ',')};${totalCount}`);

  const content = BOM + header + '\n' + rows.join('\n');
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `omzet-${month}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function OmzetOverzicht() {
  const {
    selectedMonth, omzetData, grootboekMapping, isRefreshing, refreshOmzet,
  } = useFinancieelStore();

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const grouped = groupByGrootboek(omzetData, grootboekMapping);
  const totalExcl = grouped.reduce((s, g) => s + g.total_excl, 0);
  const totalIncl = grouped.reduce((s, g) => s + g.total_incl, 0);
  const totalCount = grouped.reduce((s, g) => s + g.invoice_count, 0);

  const toggleGroup = (key: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="bg-white rounded-card shadow-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-main">Omzet {formatMonthLabel(selectedMonth)}</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => exportCSV(grouped, selectedMonth)}
            disabled={omzetData.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export CSV
          </button>
          <button
            onClick={() => refreshOmzet()}
            disabled={isRefreshing}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 disabled:opacity-50 transition-colors"
          >
            <svg className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Ververs
          </button>
        </div>
      </div>

      {omzetData.length === 0 ? (
        <p className="text-sm text-gray-400 py-4">Geen omzetdata beschikbaar voor deze maand</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50/80">
                <th className="py-2 px-3 text-left text-xs font-medium text-muted uppercase tracking-wider">Grootboek</th>
                <th className="py-2 px-3 text-left text-xs font-medium text-muted uppercase tracking-wider">Omschrijving</th>
                <th className="py-2 px-3 text-right text-xs font-medium text-muted uppercase tracking-wider">Excl. BTW</th>
                <th className="py-2 px-3 text-right text-xs font-medium text-muted uppercase tracking-wider">Incl. BTW</th>
                <th className="py-2 px-3 text-right text-xs font-medium text-muted uppercase tracking-wider">Aantal</th>
                <th className="py-2 px-3 text-right text-xs font-medium text-muted uppercase tracking-wider">%</th>
              </tr>
            </thead>
            <tbody>
              {grouped.map(group => (
                <>
                  <tr
                    key={group.key}
                    className="border-b border-gray-100 hover:bg-gray-50/50 cursor-pointer"
                    onClick={() => toggleGroup(group.key)}
                  >
                    <td className="py-2 px-3 text-sm text-gray-500">{group.grootboek_nummer}</td>
                    <td className="py-2 px-3 text-sm font-medium text-main">
                      <span className="flex items-center gap-1">
                        <svg className={`w-3 h-3 text-gray-400 transition-transform ${expandedGroups.has(group.key) ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        {group.grootboek_naam}
                        {group.segment_type && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                            group.segment_type === 'B2B'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}>{group.segment_type}</span>
                        )}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-sm text-right tabular-nums">{formatAmount(group.total_excl)}</td>
                    <td className="py-2 px-3 text-sm text-right tabular-nums">{formatAmount(group.total_incl)}</td>
                    <td className="py-2 px-3 text-sm text-right tabular-nums">{group.invoice_count}</td>
                    <td className="py-2 px-3 text-sm text-right tabular-nums text-gray-500">
                      {totalExcl > 0 ? Math.round(group.total_excl / totalExcl * 100) + '%' : '-'}
                    </td>
                  </tr>
                  {expandedGroups.has(group.key) && group.subRows.map((sub, i) => (
                    <tr key={`${group.key}-${i}`} className="border-b border-gray-50 bg-gray-50/30">
                      <td className="py-1.5 px-3 text-xs text-gray-400" />
                      <td className="py-1.5 px-3 text-xs text-gray-600 pl-8">
                        {sub.segment}{sub.category ? ` / ${sub.category}` : ''}{sub.product_name ? ` — ${sub.product_name}` : ''}
                      </td>
                      <td className="py-1.5 px-3 text-xs text-right tabular-nums text-gray-600">{formatAmount(sub.total_excl)}</td>
                      <td className="py-1.5 px-3 text-xs text-right tabular-nums text-gray-600">{formatAmount(sub.total_incl)}</td>
                      <td className="py-1.5 px-3 text-xs text-right tabular-nums text-gray-600">{sub.invoice_count}</td>
                      <td className="py-1.5 px-3 text-xs text-right tabular-nums text-gray-400">
                        {totalExcl > 0 ? Math.round(sub.total_excl / totalExcl * 100) + '%' : '-'}
                      </td>
                    </tr>
                  ))}
                </>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-300 bg-gray-50/80 font-semibold">
                <td className="py-2 px-3 text-xs uppercase text-muted" />
                <td className="py-2 px-3 text-sm">Totaal</td>
                <td className="py-2 px-3 text-sm text-right tabular-nums">{formatAmount(totalExcl)}</td>
                <td className="py-2 px-3 text-sm text-right tabular-nums">{formatAmount(totalIncl)}</td>
                <td className="py-2 px-3 text-sm text-right tabular-nums">{totalCount}</td>
                <td className="py-2 px-3 text-sm text-right tabular-nums">100%</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
